import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { DecimalPipe } from '@angular/common';

import { environment } from '../../../environments/environment';
import { Links } from '@helper/links';
import { Prices } from '@helper/prices';
import { Constants } from '@helper/constants';
import { ImageSize } from '@helper/image-size';
import { RouteHelper } from '@helper/route-paths';
import { SalesOrder_PaymentType } from '@proto/api/orders/resources';

import linkifyStr from 'linkify-string';

@Pipe({ name: 'imageUrl', standalone: true })
export class ImageUrl implements PipeTransform {
  transform(hash: string, suffix: ImageSize): string {
    if (hash.startsWith('http') || hash.startsWith('assets')) {
      return hash;
    }
    return `${environment.fileScheme}/${hash}${suffix}`;
  }
}

@Pipe({ name: 'linkify', standalone: true })
export class LinkifyPipe implements PipeTransform {
  transform(text: string): string {
    return linkifyStr(text, { target: '_blank', rel: 'noreferrer noopenner', className: 'underline-link' });
  }
}

@Pipe({ name: 'newLineToHtml', standalone: true })
export class NewLineToHtml implements PipeTransform {
  transform(value: string | undefined, pattern?: RegExp): string {
    return value ? value.replace(pattern ?? Constants.LINE_BREAK_PATTERN, '<br/>') : '';
  }
}

@Pipe({ name: 'safeHTML', standalone: true })
export class SafeHTML implements PipeTransform {
  constructor(protected sanitizer: DomSanitizer) {}

  public transform(value: string | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value ?? '');
  }
}

@Pipe({ name: 'safeUrl', standalone: true })
export class SafeUrl implements PipeTransform {
  constructor(protected sanitizer: DomSanitizer) {}

  public transform(value: string | undefined): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(value ?? '');
  }
}

@Pipe({ name: 'hintsDisplay', standalone: true })
export class HintsDisplay implements PipeTransform {
  public transform(hints: string[]): string {
    return hints.join('\n');
  }
}

@Pipe({ name: 'price', standalone: true })
export class Price implements PipeTransform {
  constructor(private decimal: DecimalPipe) {}

  public transform(price: string | number | undefined): string {
    return this.decimal.transform(Prices.price(price), '1.0-2') || Prices.DEFAULT_PRICE;
  }
}

@Pipe({ name: 'dateDisplay', standalone: true })
export class DateDisplayPipe implements PipeTransform {
  // format MM-dd
  transform(date: Date | undefined, divider = '-'): string {
    return date
      ? `${(date.getMonth() + 1).toString().padStart(2, '0')}${divider}${date.getDate().toString().padStart(2, '0')}`
      : '';
  }
}

@Pipe({ name: 'timestampDisplay', standalone: true })
export class TimestampDisplay implements PipeTransform {
  transform(
    date: Date | undefined,
    onlyDate = false,
    onlyTime = false,
    forceTime = false,
    fullTime: boolean = false
  ): string {
    if (!date) {
      return '';
    }
    // format yyyy-MM-dd hh:mm
    const time = `${date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: fullTime ? '2-digit' : undefined, // hh:mm:ss
      hour12: false,
    })}`;
    if (forceTime) {
      return time;
    }
    const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
      .getDate()
      .toString()
      .padStart(2, '0')}`;
    if (onlyTime && date.toLocaleDateString() === new Date().toLocaleDateString()) {
      return time;
    }
    return onlyDate ? dateString : `${dateString} ${time}`;
  }
}

@Pipe({ name: 'toNumber', standalone: true })
export class ToNumber implements PipeTransform {
  public transform(num: string | undefined): number {
    return !num ? 0 : Number(num);
  }
}

@Pipe({ name: 'newArray', standalone: true })
export class NewArrayPipe implements PipeTransform {
  transform(amount: number, from?: number, fill?: any): any[] {
    if (amount <= 0) {
      return [];
    }
    return fill === null || fill === undefined
      ? Array.from(Array(amount).keys()).map(k => k + (from ?? 0))
      : Array(amount).fill(fill);
  }
}

@Pipe({ name: 'phoneMask', standalone: true })
export class PhoneMask implements PipeTransform {
  public transform(data: string, num = 3): string {
    if (!data) {
      return '';
    }
    return `*******${data.slice(-num)}`;
  }
}

@Pipe({ name: 'truncatePhone', standalone: true })
export class TruncatePhone implements PipeTransform {
  public transform(phone: string): string {
    if (phone.length > 10) {
      return `*******${phone.slice(-3)}`;
    }
    return phone;
  }
}

@Pipe({ name: 'isValidNumber', standalone: true })
export class IsValidNumber implements PipeTransform {
  public transform(num: string | number | undefined): boolean {
    if (!num) {
      return false;
    }
    const converted = Number(num);
    if (isNaN(converted)) {
      return false;
    }
    return !!converted;
  }
}

@Pipe({ name: 'isGreaterThanZero', standalone: true })
export class IsGreaterThanZeroPipe implements PipeTransform {
  transform(value: string | number | undefined): boolean {
    const target = Number(value || 0);
    return !isNaN(target) && target > 0;
  }
}

@Pipe({ name: 'lastOrderId', standalone: true })
export class LastOrderId implements PipeTransform {
  public transform(orderId: string, digits: number): string {
    return orderId.slice(-digits);
  }
}

@Pipe({ name: 'isJapan', standalone: true })
export class IsJapanPipe implements PipeTransform {
  public transform(country: string): boolean {
    return country === '日本';
  }
}

@Pipe({ name: 'categoryLink', standalone: true })
export class CategoryLinkPipe implements PipeTransform {
  transform(bigId: string, mediumId: string, smallId: string): string {
    return `/${RouteHelper.category(bigId, mediumId, smallId)}`;
  }
}

@Pipe({ name: 'miniHomeLink', standalone: true })
export class MiniHomeLinkPipe implements PipeTransform {
  transform(bigName: string): string {
    return `/${RouteHelper.miniHome(bigName)}`;
  }
}

@Pipe({ name: 'handleLink', standalone: true })
export class HandleLinkPipe implements PipeTransform {
  transform(link: string): [string, { [key: string]: string }] {
    return Links.extractUrlToRouterLink(link);
  }
}

@Pipe({ name: 'typeShortTerm', standalone: true })
export class TypeShortTermPipe implements PipeTransform {
  transform(type: SalesOrder_PaymentType): string {
    switch (type) {
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA_CTBC:
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA:
      case SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED: {
        return '';
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_SEVEN_ELEVEN:
      case SalesOrder_PaymentType.PAYMENT_TYPE_FAMILY_MART: {
        return $localize`超商`;
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD_CTBC:
      case SalesOrder_PaymentType.PAYMENT_TYPE_LINE_PAY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_APPLE_PAY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CASH_ON_DELIVERY: {
        return $localize`宅配`;
      }
    }
  }
}
